import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import Quill, { Delta } from '@kim/quill';
import '@kim/quill/dist/quill.snow.css';
import { DatePicker, Select, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import {
  ClipLineIcon,
  PauseLineIcon,
  ArrowLeftLineIcon,
  ArrowRightLineIcon,
} from '@kim/ui';
import iconSend from '@src/assets/claw/chat/icon-send.svg';
import iconSendDisabled from '@src/assets/claw/chat/icon-send-disabled.svg';
import { Slot } from '@src/common/editor/blots/slot';
import {
  ChipEmbed,
  type ChipEmbedValue,
} from '@src/common/editor/blots/chipEmbed';
import { MentionBlot } from '@src/common/editor/blots/mentionBlot';
import type { TemplateNode, SelectNode } from './guideTipConfig';
import {
  buildMessageFromDelta,
  MentionDropdown,
  type MentionCollaboratorItem,
  useH5ClawMention,
} from '@src/common/claw/mention';
import { useUpload } from '@src/hooks/useUpload';
import { IFile, UploadStatusEnum } from '@src/utils/local-files-helper';
import MarioSenderFile from '@src/components/pc/MarioSenderFile';
import AgentChip, { type AgentChipProps } from './AgentChip';
import './Chatbox.less';
import { ACCEPT_FILE_TYPES } from '@/common/constants/types';
import { FileHelper } from '@/utils/file';

// ── Blot registration（模块级，只执行一次）────────────────────────────────────
Quill.register(
  {
    'formats/slot': Slot,
    'formats/chip-embed': ChipEmbed,
    'formats/mention-result': MentionBlot,
  },
  true,
);

// ─── Types ────────────────────────────────────────────────────────────────────

const FILE_URL_PREFIX = 'http://bs3-hb1.internal/is-ee-mario-velcro/';

const CLAW_CHAT_UPLOAD_URL =
  process.env.NODE_ENV === 'production'
    ? window.location.origin + '/mfp/api/v1/mario/upload'
    : '/mfp/api/v1/mario/upload';

// ─── 草稿存储（保存完整 Delta，支持 @mention / slot / chip-embed）─────────────

const DRAFT_KEY_PREFIX = 'claw-chatbox-draft:';

function isDeltaEmpty(delta: Delta): boolean {
  const ops = delta.ops ?? [];
  if (ops.length === 0) return true;
  if (ops.length === 1 && ops[0].insert === '\n') return true;
  return false;
}

function saveDraft(sessionKey: string, delta: Delta): void {
  if (!sessionKey) return;
  try {
    if (isDeltaEmpty(delta)) {
      localStorage.removeItem(`${DRAFT_KEY_PREFIX}${sessionKey}`);
    } else {
      localStorage.setItem(
        `${DRAFT_KEY_PREFIX}${sessionKey}`,
        JSON.stringify(delta.ops),
      );
    }
  } catch {
    /* ignore */
  }
}

/** 返回 Delta，无草稿时返回 null */
function loadDraft(sessionKey: string): Delta | null {
  if (!sessionKey) return null;
  try {
    const raw = localStorage.getItem(`${DRAFT_KEY_PREFIX}${sessionKey}`);
    if (!raw) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const delta = new Delta(JSON.parse(raw) as any[]);
    return isDeltaEmpty(delta) ? null : delta;
  } catch {
    return null;
  }
}

function clearDraft(sessionKey: string): void {
  if (!sessionKey) return;
  try {
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${sessionKey}`);
  } catch {
    /* ignore */
  }
}

interface ActiveChip {
  type: 'select' | 'datetime';
  key: string;
  options?: string[];
  rect: DOMRect;
  el: HTMLElement;
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * 将 TemplateNode[] 转换为 Quill Delta：
 * - text       → 纯文本 insert
 * - input/mention → slot format insert（可直接在 Quill 内编辑）
 * - select/datetime → chip-embed insert（不可直接编辑，点击弹层）
 */
function nodesToDelta(nodes: TemplateNode[]): Delta {
  const delta = new Delta();
  for (const node of nodes) {
    if (node.type === 'text') {
      delta.insert(node.content);
    } else if (node.type === 'input' || node.type === 'mention') {
      delta.insert(node.placeholder, { slot: node.placeholder });
    } else if (node.type === 'select') {
      delta.insert({
        'chip-embed': {
          type: 'select',
          key: node.key,
          placeholder: node.placeholder,
          options: (node as SelectNode).options,
        } as ChipEmbedValue,
      });
    } else {
      // datetime
      delta.insert({
        'chip-embed': {
          type: 'datetime',
          key: node.key,
          placeholder: node.placeholder,
        } as ChipEmbedValue,
      });
    }
  }
  return delta;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface PrefilledText {
  text: string;
  /** 每次设置时递增，确保重复相同文字也能触发 effect */
  nonce: number;
}

export interface ChatboxProps {
  className?: string;
  template?: TemplateNode[];
  placeholder?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /** 当前是否处于回答中（控制中止按钮与发送可用性，不禁用输入） */
  isResponding?: boolean;
  onEditorEmptyChange?: (isEmpty: boolean) => void;
  onSubmit?: (message: string) => void;
  onClearTemplate?: () => void;
  /** 流式输出中止回调；传入后，disabled 状态下显示中止按钮 */
  onAbort?: () => void;
  /** 预填文本（不发送，仅写入编辑器供用户编辑）*/
  prefilledText?: PrefilledText;
  /** 每次递增时，强制清空编辑器所有内容 */
  clearEditorNonce?: number;
  /** 可选：输入框旁 Agent 标签，删除时在 Popover 内二次确认（与 {@link AgentChipProps} 一致） */
  agentChip?: AgentChipProps;
  /** 为 true 时隐藏附件上传入口，并禁用拖拽/粘贴上传文件 */
  hideAttachmentUpload?: boolean;
  /** 可选：外部扩展 @ 协作者搜索，传入后会在下拉中展示协作者 Tab */
  searchCollaborators?: (query: string) => Promise<MentionCollaboratorItem[]>;
  /** 当前会话 key，用于按会话保存/恢复草稿 */
  sessionKey?: string;
}

const Chatbox = forwardRef<{ clearEditor: () => void }, ChatboxProps>(
  (
    {
      className,
      template,
      placeholder = '今天有什么我可以帮你的？（输入 @ 引用内容）',
      disabled = false,
      onClick,
      isResponding = false,
      onEditorEmptyChange,
      onSubmit,
      onClearTemplate,
      onAbort,
      prefilledText,
      clearEditorNonce,
      agentChip,
      hideAttachmentUpload = false,
      searchCollaborators,
      sessionKey,
    },
    ref,
  ) => {
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachmentsRef = useRef<HTMLDivElement>(null);
    const chatboxRef = useRef<HTMLDivElement>(null);

    const {
      files,
      isUploading,
      deleteFile,
      beforeUpload,
      customRequest,
      clearFiles,
    } = useUpload({ canDrag: false, uploadUrl: CLAW_CHAT_UPLOAD_URL });
    const filesRef = useRef<IFile[]>([]);
    useEffect(() => {
      filesRef.current = files;
    }, [files]);

    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkAttachmentScroll = useCallback(() => {
      const el = attachmentsRef.current;
      if (!el) return;
      setShowLeftArrow(el.scrollLeft > 0);
      setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
      const el = attachmentsRef.current;
      if (!el) return;
      el.addEventListener('scroll', checkAttachmentScroll);
      return () => el.removeEventListener('scroll', checkAttachmentScroll);
    }, [checkAttachmentScroll]);

    useEffect(() => {
      setTimeout(checkAttachmentScroll, 0);
    }, [files, checkAttachmentScroll]);

    const [chipValues, setChipValues] = useState<Record<string, string>>({});
    const [hasContent, setHasContent] = useState(false);
    const [activeChip, setActiveChip] = useState<ActiveChip | null>(null);

    const {
      activeMention,
      mentionResults,
      mentionLoading,
      mentionLoadingMore,
      mentionHasMore,
      mentionActiveIdx,
      handleMentionSelect,
      loadMoreMentions,
      applyMentionFromQuillChange,
      disposeMentionTimers,
    } = useH5ClawMention({
      quillRef,
      editorContainerRef,
      chatboxRef,
      searchCollaborators,
    });

    // 用 ref 持有最新值，避免 Quill 回调闭包中的 stale state
    const chipValuesRef = useRef(chipValues);
    const templateRef = useRef(template);
    const onSubmitRef = useRef(onSubmit);
    const onClearTemplateRef = useRef(onClearTemplate);
    const onEditorEmptyChangeRef = useRef(onEditorEmptyChange);
    const disabledRef = useRef(disabled);
    const isUploadingRef = useRef(isUploading);

    useEffect(() => {
      chipValuesRef.current = chipValues;
    }, [chipValues]);
    useEffect(() => {
      templateRef.current = template;
    }, [template]);
    useEffect(() => {
      onSubmitRef.current = onSubmit;
    }, [onSubmit]);
    useEffect(() => {
      onClearTemplateRef.current = onClearTemplate;
    }, [onClearTemplate]);
    useEffect(() => {
      onEditorEmptyChangeRef.current = onEditorEmptyChange;
    }, [onEditorEmptyChange]);
    useEffect(() => {
      disabledRef.current = disabled;
    }, [disabled]);
    useEffect(() => {
      isUploadingRef.current = isUploading;
    }, [isUploading]);

    const applyMentionFromQuillChangeRef = useRef(applyMentionFromQuillChange);
    useEffect(() => {
      applyMentionFromQuillChangeRef.current = applyMentionFromQuillChange;
    }, [applyMentionFromQuillChange]);

    const clearEditor = useCallback(() => {
      if (!quillRef.current) return;
      quillRef.current.setText('');
      setHasContent(false);
      onEditorEmptyChangeRef.current?.(true);
    }, []);
    // ── 草稿 refs ─────────────────────────────────────────────────────────────
    const sessionKeyRef = useRef(sessionKey);
    const prevSessionKeyRef = useRef<string | undefined>(undefined);
    const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    useEffect(() => {
      sessionKeyRef.current = sessionKey;
    }, [sessionKey]);

    useImperativeHandle(
      ref,
      () => ({
        clearEditor,
      }),
      [clearEditor],
    );

    // ── handleSubmit（通过 ref 供 Quill keyboard binding 调用）─────────────────

    const handleSubmit = useCallback(() => {
      const quill = quillRef.current;
      if (!quill || disabledRef.current || isUploadingRef.current) return;

      const currentTemplate = templateRef.current;

      // 无论模板还是纯文本模式，统一走 delta 解析，保证 mention-result embed 能输出 id
      const textMsg = buildMessageFromDelta(quill, chipValuesRef.current);

      // 将上传成功的文件拼为 md 链接，每个文件独占一段，与正文之间用分割线隔开
      const successFiles = filesRef.current.filter(
        (f) =>
          f.status === UploadStatusEnum.SUCCESS && (f.fileUrl || f.objectKey),
      );
      // 每条链接之间加空行，确保 markdown 渲染为独立段落而非内联
      const uploadedLinks = successFiles
        .map(
          (f) =>
            `[${f.name}](${f.fileUrl || `${FILE_URL_PREFIX}${f.objectKey}`})`,
        )
        .join('\n\n');

      const parts: string[] = [];
      if (textMsg) parts.push(textMsg);
      if (uploadedLinks) {
        // 用空行包裹 --- 避免 setext heading 语法（text\n--- 会变成 <h2>）
        if (textMsg) parts.push('\n---');
        parts.push(uploadedLinks);
      }
      const finalMsg = parts.join('\n\n');
      if (!finalMsg) return;

      onSubmitRef.current?.(finalMsg);
      onClearTemplateRef.current?.();

      if (!currentTemplate) {
        quill.setText('');
        setHasContent(false);
        onEditorEmptyChangeRef.current?.(true);
      }
      // 发送成功后清除草稿
      if (sessionKeyRef.current) clearDraft(sessionKeyRef.current);
      clearFiles();
      setActiveChip(null);
    }, [clearFiles]); // clearFiles 来自 useUpload，引用稳定

    const handleSubmitRef = useRef(handleSubmit);
    useEffect(() => {
      handleSubmitRef.current = handleSubmit;
    }, [handleSubmit]);

    /** 点击 @ 按钮：在光标处插入 @，并触发 mention 搜索 */
    const handleInsertAt = useCallback(() => {
      const quill = quillRef.current;
      if (!quill || disabledRef.current) return;
      const sel = quill.getSelection();
      const index = sel ? sel.index : quill.getLength() - 1;
      quill.insertText(index, '@', 'user');
      quill.setSelection(index + 1, 0);
      quill.focus();
    }, []);

    // ── Quill 初始化（仅执行一次）────────────────────────────────────────────────

    useEffect(() => {
      const container = editorContainerRef.current;
      if (!container || quillRef.current) return;

      const quill = new Quill(container, {
        theme: 'snow',
        modules: {
          toolbar: false,
          keyboard: {
            bindings: {
              enter: {
                key: 'Enter',
                handler: (): boolean => {
                  handleSubmitRef.current();
                  return false;
                },
              },
            },
          },
        },
        formats: ['slot', 'chip-embed', 'mention-result'],
        placeholder,
      });

      quill.root.setAttribute('spellcheck', 'false');

      // 监听内容变化，更新 hasContent，同时检测 @ 触发
      const onTextChange = (_delta: unknown, _old: unknown, source: string) => {
        const isEmpty = quill.getLength() <= 1;
        setHasContent(!isEmpty);
        onEditorEmptyChangeRef.current?.(isEmpty);
        // 更新所有 slot 节点的 data-filled 状态（文字与 placeholder 不同则视为已填写）
        container
          .querySelectorAll<HTMLElement>('.mario-slot')
          .forEach((slotEl) => {
            const isFilled =
              (slotEl.textContent ?? '') !== (slotEl.dataset.placeholder ?? '');
            slotEl.dataset.filled = isFilled ? 'true' : 'false';
          });
        if (source !== 'user') return;
        // 用户手动清空内容时重置模板状态
        if (isEmpty && templateRef.current) {
          onClearTemplateRef.current?.();
          setChipValues({});
        }
        // 防抖保存草稿（模板模式下不保存，保存完整 Delta 以保留 @mention / slot 等）
        if (!templateRef.current && sessionKeyRef.current) {
          if (draftSaveTimerRef.current)
            clearTimeout(draftSaveTimerRef.current);
          draftSaveTimerRef.current = setTimeout(() => {
            const key = sessionKeyRef.current;
            if (!key) return;
            saveDraft(key, quill.getContents());
          }, 600);
        }
        // Quill 在 text-change 同步阶段里 getSelection() 常为 null，延后一帧再检测 @ 才能稳定弹出 mention
        queueMicrotask(() => applyMentionFromQuillChangeRef.current(quill));
      };
      quill.on('text-change', onTextChange);

      // 点击 chip-embed 节点时打开 picker；点击 slot(input) 节点时全选
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // slot（input/mention）：全选内容，用户直接输入即可替换 placeholder
        const slotEl = target.closest('.mario-slot') as HTMLElement | null;
        if (slotEl) {
          const blot = Quill.find(slotEl);
          if (blot) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const idx = quill.getIndex(blot as any);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const len =
              (blot as any).length?.() ?? slotEl.textContent?.length ?? 0;
            quill.setSelection(idx, len, 'user');
          }
          return;
        }

        const embedEl = target.closest(
          '.chatbox-chip-embed',
        ) as HTMLElement | null;
        if (!embedEl) {
          setActiveChip(null);
          return;
        }
        setActiveChip({
          type: (embedEl.dataset.chipType ?? 'select') as 'select' | 'datetime',
          key: embedEl.dataset.chipKey ?? '',
          options: embedEl.dataset.chipOptions
            ? (JSON.parse(embedEl.dataset.chipOptions) as string[])
            : undefined,
          rect: embedEl.getBoundingClientRect(),
          el: embedEl,
        });
      };
      container.addEventListener('click', handleClick);

      quillRef.current = quill;
      onEditorEmptyChangeRef.current?.(true);

      return () => {
        quill.off(
          'text-change',
          onTextChange as Parameters<typeof quill.off>[1],
        );
        container.removeEventListener('click', handleClick);
        disposeMentionTimers();
        quillRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disposeMentionTimers]);

    // ── 同步 disabled 状态 ───────────────────────────────────────────────────────

    useEffect(() => {
      const quill = quillRef.current;
      if (!quill) return;
      if (disabled) quill.disable();
      else quill.enable();
    }, [disabled]);

    // ── 同步 placeholder ─────────────────────────────────────────────────────────

    useEffect(() => {
      const quill = quillRef.current;
      if (quill) quill.root.dataset.placeholder = placeholder;
    }, [placeholder]);
    // ── 强制清空编辑器（clearEditorNonce 每次递增时触发）────────────────────────────
    // 注意：sessionKey 和 clearEditorNonce 在不同 render 中变化，clearEditorNonce 在后。
    // 若此时有对应草稿，应恢复草稿而非清空，否则草稿会被覆盖。

    useEffect(() => {
      if (clearEditorNonce === undefined) return;
      const quill = quillRef.current;
      if (!quill) return;
      const key = sessionKeyRef.current;
      const draft = key ? loadDraft(key) : null;
      if (draft) {
        quill.setContents(draft);
        quill.setSelection(quill.getLength() - 1, 0);
        setHasContent(true);
        onEditorEmptyChangeRef.current?.(false);
      } else {
        quill.setContents(new Delta().insert('\n'));
        setHasContent(false);
        onEditorEmptyChangeRef.current?.(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearEditorNonce]);

    // ── sessionKey 切换：保存旧会话草稿，恢复新会话草稿 ─────────────────────────

    useEffect(() => {
      const prev = prevSessionKeyRef.current;
      prevSessionKeyRef.current = sessionKey;

      const quill = quillRef.current;
      if (!quill) return;
      // 模板模式下不操作草稿
      if (templateRef.current) return;

      // 保存旧会话的草稿（仅在会话真正切换时）
      if (prev !== undefined && prev !== sessionKey && prev) {
        if (draftSaveTimerRef.current) {
          clearTimeout(draftSaveTimerRef.current);
          draftSaveTimerRef.current = null;
        }
        saveDraft(prev, quill.getContents());
      }

      // 恢复新会话的草稿
      if (sessionKey) {
        const draft = loadDraft(sessionKey);
        if (draft) {
          quill.setContents(draft);
          quill.setSelection(quill.getLength() - 1, 0);
          setHasContent(true);
          onEditorEmptyChangeRef.current?.(false);
        } else if (prev !== undefined && prev !== sessionKey) {
          // 切换到无草稿的新会话时清空编辑器
          quill.setContents(new Delta().insert('\n'));
          setHasContent(false);
          onEditorEmptyChangeRef.current?.(true);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionKey]);

    // ── 同步 template（加载 Delta） ──────────────────────────────────────────────

    // ── 强制清空编辑器（clearEditorNonce 每次递增时触发）────────────────────────────

    useEffect(() => {
      if (clearEditorNonce === undefined) return;
      const quill = quillRef.current;
      if (!quill) return;
      quill.setContents(new Delta().insert('\n'));
      setHasContent(false);
      onEditorEmptyChangeRef.current?.(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearEditorNonce]);

    // ── 同步 template（加载 Delta） ──────────────────────────────────────────────

    useEffect(() => {
      const quill = quillRef.current;
      if (!quill) return;

      setActiveChip(null);

      if (!template) {
        quill.setContents(new Delta().insert('\n'));
        setChipValues({});
        setHasContent(false);
        onEditorEmptyChangeRef.current?.(true);
        requestAnimationFrame(() => quill.focus());
        return;
      }

      quill.setContents(nodesToDelta(template));
      // 初始化所有 slot 为未填写状态
      editorContainerRef.current
        ?.querySelectorAll<HTMLElement>('.mario-slot')
        .forEach((el) => {
          el.dataset.filled = 'false';
        });
      setHasContent(true);
      onEditorEmptyChangeRef.current?.(false);

      // 初始化有 defaultValue 的 select/datetime chip
      const initial: Record<string, string> = {};
      template.forEach((node) => {
        if (
          (node.type === 'select' || node.type === 'datetime') &&
          node.defaultValue
        ) {
          initial[node.key] = node.defaultValue;
          const embedEl = editorContainerRef.current?.querySelector(
            `[data-chip-key="${node.key}"]`,
          ) as HTMLElement | null;
          if (embedEl) {
            embedEl.textContent = node.defaultValue;
            embedEl.classList.add('chatbox-chip-embed--active');
          }
        }
      });
      setChipValues(initial);

      // 将 mention 节点的 mentionType 写入对应 slot DOM，供 getSlotMentionType 精确识别
      requestAnimationFrame(() => {
        template.forEach((node) => {
          if (node.type === 'mention' && node.mentionType) {
            const slotEl = editorContainerRef.current?.querySelector(
              `[data-placeholder="${node.placeholder}"]`,
            ) as HTMLElement | null;
            if (slotEl) slotEl.dataset.mentionType = node.mentionType;
          }
        });
      });

      // 模板加载后只聚焦编辑器，不默认选中任何 slot，
      // 避免 placeholder 文本出现蓝色高亮选中态。
      requestAnimationFrame(() => {
        quill.focus();
        quill.setSelection(quill.getLength() - 1, 0);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template]);

    // ── 预填文本（点击 suggestion 后写入编辑器，不发送）────────────────────────────
    // 必须在 template effect 之后声明，保证同一 render 中 template 清空先执行，再填充文本

    useEffect(() => {
      const quill = quillRef.current;
      if (!prefilledText || !quill) return;
      quill.setText(prefilledText.text);
      // 光标移到末尾
      quill.setSelection(quill.getLength() - 1, 0);
      setHasContent(quill.getLength() > 1);
      onEditorEmptyChangeRef.current?.(quill.getLength() <= 1);
      // 清空模板（预填文本和模板模式互斥）
      onClearTemplateRef.current?.();
      // nonce 变化时才触发，text 本身不作为依赖（避免用户编辑时重置）
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefilledText?.nonce]);

    // ── 点击 picker 之外关闭弹层 ─────────────────────────────────────────────────

    useEffect(() => {
      if (!activeChip) return;
      const handleMousedown = (e: MouseEvent) => {
        const target = e.target as Element;
        if (
          target.closest('.ant-select-dropdown') ||
          target.closest('.ant-picker-dropdown') ||
          target.closest('.ant-popover') ||
          target.closest('.chatbox-chip-embed')
        )
          return;
        setActiveChip(null);
      };
      // 延迟绑定，防止打开瞬间触发关闭
      const timer = setTimeout(
        () => document.addEventListener('mousedown', handleMousedown),
        50,
      );
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleMousedown);
      };
    }, [activeChip]);

    // ── chip value 更新（同时更新 DOM 显示文字）──────────────────────────────────

    const updateChipValue = useCallback(
      (key: string, value: string, el: HTMLElement) => {
        setChipValues((prev) => ({ ...prev, [key]: value }));
        el.textContent = value || (el.dataset.chipPlaceholder ?? '');
        el.classList.toggle('chatbox-chip-embed--active', !!value);
      },
      [],
    );

    // ── 文件附件 ──────────────────────────────────────────────────────────────────

    const uploadFiles = useCallback(
      async (fileList: File[] | FileList) => {
        for (const rawFile of Array.from(fileList)) {
          const file = rawFile as unknown as IFile;
          file.uid = `${Date.now()}-${Math.random()}`;
          if (beforeUpload(file)) {
            await customRequest(file);
          }
        }
      },
      [beforeUpload, customRequest],
    );

    const handleFileSelect = useCallback(
      async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        await uploadFiles(fileList);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      [uploadFiles],
    );

    // ── 粘贴图片/文件 ─────────────────────────────────────────────────────────────

    useEffect(() => {
      const el = chatboxRef.current;
      if (!el) return;
      const handlePaste = (e: ClipboardEvent) => {
        if (disabledRef.current) return;
        if (hideAttachmentUpload) return;
        const items = e.clipboardData?.items;
        if (!items) return;
        const fileItems: File[] = [];
        for (const item of Array.from(items)) {
          if (item.kind === 'file') {
            const f = item.getAsFile();
            if (!f) continue;
            // 剪贴板截图多为固定名 image.png，仅按原名会与列表中其它附件撞名被误判为重复
            const dot = f.name.lastIndexOf('.');
            const ext =
              dot >= 0
                ? f.name.slice(dot)
                : f.type?.split('/')[1]
                  ? `.${f.type.split('/')[1]}`
                  : '';
            const uniqueBase = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            const uniqueName = ext ? `${uniqueBase}${ext}` : uniqueBase;
            fileItems.push(
              new File([f], uniqueName, {
                type: f.type,
                lastModified: f.lastModified,
              }),
            );
          }
        }
        if (fileItems.length > 0) {
          e.preventDefault();
          uploadFiles(fileItems);
        }
      };
      el.addEventListener('paste', handlePaste);
      return () => el.removeEventListener('paste', handlePaste);
    }, [uploadFiles, hideAttachmentUpload]);

    // ── 拖拽上传文件 ──────────────────────────────────────────────────────────────

    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        if (hideAttachmentUpload) return;
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.types.includes('Files')) {
          setIsDragging(true);
        }
      },
      [hideAttachmentUpload],
    );

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        if (hideAttachmentUpload) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
      },
      [hideAttachmentUpload],
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        if (hideAttachmentUpload) return;
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
          dragCounterRef.current = 0;
          setIsDragging(false);
        }
      },
      [hideAttachmentUpload],
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        if (hideAttachmentUpload) return;
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        if (disabledRef.current) return;
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          uploadFiles(files);
        }
      },
      [uploadFiles, hideAttachmentUpload],
    );

    const removeAttachment = useCallback(
      (uid: string) => {
        deleteFile(uid);
      },
      [deleteFile],
    );

    const accept = ACCEPT_FILE_TYPES.filter(
      (type) => !FileHelper.notAllowedTypes.includes(type),
    )
      .map((type) => `.${type}`)
      .join(',');

    // ── Render ────────────────────────────────────────────────────────────────────

    const hasUploadedFile = files.some(
      (f) => f.status === UploadStatusEnum.SUCCESS,
    );
    const canSend =
      (hasContent || !!template || hasUploadedFile) &&
      !disabled &&
      !isResponding &&
      !isUploading;

    return (
      <div
        className={`chatbox-wrapper ${disabled ? 'chatbox-wrapper--disabled' : ''} `}
        onClick={onClick}
      >
        <div
          ref={chatboxRef}
          className={`chatbox ${disabled ? 'chatbox--disabled' : ''} ${isDragging ? 'chatbox--dragging' : ''} ${className ?? ''}`}
          onDragEnter={hideAttachmentUpload ? undefined : handleDragEnter}
          onDragOver={hideAttachmentUpload ? undefined : handleDragOver}
          onDragLeave={hideAttachmentUpload ? undefined : handleDragLeave}
          onDrop={hideAttachmentUpload ? undefined : handleDrop}
        >
          {/* 附件预览区（与 SenderHeader 保持一致，复用 MarioSenderFile） */}
          {files.length > 0 && (
            <div className="chatbox-attachments-wrap">
              {showLeftArrow && (
                <button
                  type="button"
                  className="chatbox-attachments-arrow chatbox-attachments-arrow--left"
                  onClick={() =>
                    attachmentsRef.current?.scrollBy({
                      left: -200,
                      behavior: 'smooth',
                    })
                  }
                >
                  <ArrowLeftLineIcon size={10} />
                </button>
              )}
              <div ref={attachmentsRef} className="chatbox-attachments">
                {files.map((f) => (
                  <MarioSenderFile
                    key={f.uid}
                    file={f}
                    progress={f.percent ?? 0}
                    uploading={f.status === UploadStatusEnum.UPLOADING}
                    status={f.status}
                    onHandleDeleteFile={() => removeAttachment(f.uid)}
                    onHandleRetryUpload={() => removeAttachment(f.uid)}
                  />
                ))}
              </div>
              {showRightArrow && (
                <button
                  type="button"
                  className="chatbox-attachments-arrow chatbox-attachments-arrow--right"
                  onClick={() =>
                    attachmentsRef.current?.scrollBy({
                      left: 200,
                      behavior: 'smooth',
                    })
                  }
                >
                  <ArrowRightLineIcon size={10} />
                </button>
              )}
            </div>
          )}

          <div className="chatbox-inner">
            <div className="chatbox-body">
              {agentChip?.name?.trim() ? (
                <AgentChip {...agentChip} disabled={disabled} />
              ) : null}
              {/* Quill 挂载点（同时承载纯文本和模板两种模式） */}
              <div ref={editorContainerRef} className="chatbox-quill-editor" />
            </div>

            <div className="chatbox-actions">
              {!hideAttachmentUpload && (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={accept}
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
              )}
              <Tooltip title="想要找到谁？ 试试 @" placement="top">
                <button
                  className="chatbox-action-at"
                  onClick={handleInsertAt}
                  type="button"
                  disabled={disabled}
                >
                  @
                </button>
              </Tooltip>
              {!hideAttachmentUpload && (
                <Tooltip title="添加附件" placement="top">
                  <button
                    className="chatbox-action-attach"
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                    title="添加附件"
                  >
                    <ClipLineIcon size={16} />
                  </button>
                </Tooltip>
              )}
              {isResponding && onAbort ? (
                <button
                  className="chatbox-action-abort"
                  onClick={onAbort}
                  type="button"
                  title="中止生成"
                >
                  <PauseLineIcon size={16} />
                </button>
              ) : (
                <button
                  className={`chatbox-action-send${canSend ? ' chatbox-action-send--active' : ''}`}
                  onClick={handleSubmit}
                  type="button"
                  disabled={!canSend}
                >
                  <img src={canSend ? iconSend : iconSendDisabled} alt="发送" />
                </button>
              )}
            </div>
          </div>

          {/* 拖拽遮罩 */}
          {!hideAttachmentUpload && isDragging && (
            <div className="chatbox-drag-overlay">
              <span className="chatbox-drag-overlay__text">
                松开鼠标上传文件
              </span>
            </div>
          )}

          {/* @ mention 搜索下拉 */}
          {activeMention && (
            <MentionDropdown
              items={mentionResults}
              query={activeMention.query}
              loading={mentionLoading}
              loadingMore={mentionLoadingMore}
              hasMore={mentionHasMore}
              activeIndex={mentionActiveIdx}
              anchorRect={activeMention.anchorRect}
              onSelect={handleMentionSelect}
              onLoadMore={loadMoreMentions}
              showCollaboratorTab={!!searchCollaborators}
            />
          )}

          {/* select / datetime 弹层（portal 到 body，按 chip 位置定位） */}
          {activeChip &&
            createPortal(
              <div
                className="chatbox-chip-picker"
                style={{
                  position: 'fixed',
                  left: activeChip.rect.left,
                  top: activeChip.rect.bottom + 4,
                  zIndex: 9999,
                }}
              >
                {activeChip.type === 'select' ? (
                  <Select
                    open
                    popupMatchSelectWidth={false}
                    options={activeChip.options?.map((o) => ({
                      label: o,
                      value: o,
                    }))}
                    value={chipValues[activeChip.key] || undefined}
                    onChange={(val: string) => {
                      updateChipValue(activeChip.key, val, activeChip.el);
                      setActiveChip(null);
                    }}
                    onDropdownVisibleChange={(visible) => {
                      if (!visible) setActiveChip(null);
                    }}
                    style={{ minWidth: 120 }}
                  />
                ) : (
                  <DatePicker
                    open
                    showTime
                    value={null}
                    onChange={(date: Dayjs | null) => {
                      updateChipValue(
                        activeChip.key,
                        date ? date.format('M月D日 HH:mm') : '',
                        activeChip.el,
                      );
                      setActiveChip(null);
                    }}
                    onOpenChange={(visible) => {
                      if (!visible) setActiveChip(null);
                    }}
                    // trigger 本身收起，picker popup 悬浮在上方
                    style={{
                      width: 0,
                      overflow: 'hidden',
                      padding: 0,
                      border: 'none',
                    }}
                  />
                )}
              </div>,
              document.body,
            )}
        </div>
      </div>
    );
  },
);

export type { AgentChipProps } from './AgentChip';

export default Chatbox;
